import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBank, listGroups, updateBank } from '../api/banks';
import './BanqueEditModal.css';

// Modal "Modifier" pour Parametres > Banque (remplace l'ancienne navigation
// vers une page dediee /parametres/banque/:id, meme champs editables).
const BanqueEditModal = ({ bankId, onClose, onSaved }) => {
  const [bank, setBank] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([getBank(bankId), listGroups()])
      .then(([bankData, groupsData]) => {
        if (cancelled) return;
        setBank(bankData);
        setGroups(groupsData);
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger ce bank.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bankId]);

  const updateField = (field, value) => {
    setBank((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updated = await updateBank(bankId, {
        name: bank.name,
        address: bank.address,
        phone: bank.phone,
        comment: bank.comment,
        group: bank.group || null,
        sale_enabled: bank.sale_enabled,
        can_nullify: bank.can_nullify,
      });
      onSaved(updated);
      onClose();
    } catch (err) {
      setError('Sauvegarde impossible — verifie les champs et le serveur backend.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bem-overlay">
      <div className="bem-modal">
        <div className="bem-header">
          <span className="bem-title">Modifier Banque</span>
          <button className="bem-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="bem-body">
          {loading && <p className="bem-status">Chargement...</p>}
          {!loading && !bank && <p className="bem-status bem-error">{error || 'Bank introuvable.'}</p>}

          {!loading && bank && (
            <form id="bem-form" onSubmit={handleSave}>
              <div className="bem-row">
                <label className="bem-label">ID</label>
                <span className="bem-value">
                  {bank.seq}
                  {/* Meme valeur que "REF:" imprime sur les fiches du POS
                      (Bank.seq repete sur 2 chiffres, ex. 4 -> 04-04). */}
                  {bank.seq != null && (
                    <> &nbsp;—&nbsp; Ref fiche : <strong>{String(bank.seq).padStart(2, '0')}-{String(bank.seq).padStart(2, '0')}</strong></>
                  )}
                </span>
              </div>

              <div className="bem-row">
                <label className="bem-label">Username</label>
                <span className="bem-value">{bank.username || '—'}</span>
              </div>

              <div className="bem-row">
                <label className="bem-label">Group</label>
                <select
                  className="bem-input"
                  value={bank.group ?? ''}
                  onChange={(e) => updateField('group', e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">—</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.display_name || g.name}</option>
                  ))}
                </select>
              </div>

              <div className="bem-row">
                <label className="bem-label">Superviseur(s)</label>
                <span className="bem-value">
                  {(bank.supervisor_names || []).join(', ') || '—'}
                  {' '}
                  <Link to="/parametres/superviseurs" className="bem-link" onClick={onClose}>(gérer)</Link>
                </span>
              </div>

              <div className="bem-row">
                <label className="bem-label">Name</label>
                <input
                  className="bem-input"
                  type="text"
                  value={bank.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
              </div>

              <div className="bem-row">
                <label className="bem-label">Address</label>
                <input
                  className="bem-input"
                  type="text"
                  value={bank.address}
                  onChange={(e) => updateField('address', e.target.value)}
                />
              </div>

              <div className="bem-row">
                <label className="bem-label">Phone</label>
                <input
                  className="bem-input"
                  type="text"
                  value={bank.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>

              <div className="bem-row bem-row-top">
                <label className="bem-label">Comments</label>
                <textarea
                  className="bem-textarea"
                  value={bank.comment}
                  onChange={(e) => updateField('comment', e.target.value)}
                />
              </div>

              <label className="bem-checkbox-label">
                <input
                  type="checkbox"
                  checked={bank.sale_enabled}
                  onChange={(e) => updateField('sale_enabled', e.target.checked)}
                />
                Enable Sale?
              </label>

              <label className="bem-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!bank.can_nullify}
                  onChange={(e) => updateField('can_nullify', e.target.checked)}
                />
                Autoriser l&apos;annulation de fiches (POS)
              </label>

              {error && <p className="bem-error">{error}</p>}
            </form>
          )}
        </div>

        <div className="bem-footer">
          <button className="bem-cancel-btn" onClick={onClose}>Annuler</button>
          {!loading && bank && (
            <button type="submit" form="bem-form" className="bem-save-btn" disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Sauver'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BanqueEditModal;
